import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { customerService, Customer } from '../services/customers';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import dayjs from 'dayjs';

const { Title } = Typography;


type FilterType = 'all' | 'potential' | 'active' | 'inactive';

export const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'personal.view');
  const canCreate = hasPermission(user, 'personal.create');

  useEffect(() => {
    if (!canView) {
      setCustomers([]);
      return;
    }
    fetchCustomers();
  }, [canView]);

  const fetchCustomers = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await customerService.getAll();
      setCustomers(response.data || []);
    } catch (error) {
      message.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate('/customers/new');
  };

  const handleImportNavigate = () => {
    navigate('/customers/import');
  };

  const handleEdit = (customer: Customer) => {
    if (!canView) {
      return;
    }
    navigate(`/customers/${customer.id}/edit`);
  };

  const handleDelete = async (id: number) => {
    try {
      await customerService.delete(id);
      message.success('Customer deleted');
      fetchCustomers();
    } catch {
      message.error('Failed to delete customer');
    }
  };

  const filteredCustomers = useMemo(() => {
    if (filter === 'all') {
      return customers;
    } else if (filter === 'potential') {
      return customers.filter(customer => customer.type === 'potential');
    } else if (filter === 'active') {
      return customers.filter(customer => customer.type === 'active' || (customer as any).type === 'existing');
    } else if (filter === 'inactive') {
      return customers.filter(customer => customer.type === 'inactive' || (customer as any).type === 'resigned');
    }
    return customers;
  }, [customers, filter]);

  const baseColumns = [
    { 
      title: 'Customer', 
      dataIndex: 'name', 
      key: 'name', 
      width: 220,
      sorter: (a: Customer, b: Customer) => (a.name || '').localeCompare(b.name || ''),
      render: (name: string) => (
        <Tooltip title={name || '-'} placement="topLeft">
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name || '-'}
          </div>
        </Tooltip>
      ),
    },
    { 
      title: 'Identification No.', 
      dataIndex: 'ic_passport_no', 
      key: 'ic_passport_no', 
      width: 200,
      sorter: (a: Customer, b: Customer) => (a.ic_passport_no || '').localeCompare(b.ic_passport_no || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Tin No.', 
      dataIndex: 'tin_no', 
      key: 'tin_no', 
      width: 160,
      sorter: (a: Customer, b: Customer) => (a.tin_no || '').localeCompare(b.tin_no || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Nationality', 
      dataIndex: 'nationality', 
      key: 'nationality', 
      width: 160,
      sorter: (a: Customer, b: Customer) => (a.nationality || '').localeCompare(b.nationality || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Race', 
      dataIndex: 'race', 
      key: 'race', 
      width: 140,
      sorter: (a: Customer, b: Customer) => (a.race || '').localeCompare(b.race || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Sex', 
      dataIndex: 'sex', 
      key: 'sex', 
      width: 120,
      sorter: (a: Customer, b: Customer) => (a.sex || '').localeCompare(b.sex || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    { 
      title: 'Engagement', 
      dataIndex: 'engagement', 
      key: 'engagement', 
      width: 200,
      sorter: (a: Customer, b: Customer) => (a.engagement || '').localeCompare(b.engagement || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Agent',
      dataIndex: 'agent',
      key: 'agent',
      width: 160,
      sorter: (a: Customer, b: Customer) => (a.agent?.name || '').localeCompare(b.agent?.name || ''),
      render: (agent: Customer['agent']) => {
        const agentName = agent?.name || '-';
        return (
          <Tooltip title={agentName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {agentName}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 160,
      sorter: (a: Customer, b: Customer) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (team: Customer['team']) => {
        const teamName = team?.name || '-';
        return (
          <Tooltip title={teamName} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {teamName}
    </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      visible: false,
      sorter: (a: Customer, b: Customer) => (a.type || '').localeCompare(b.type || ''),
      render: (value: string) => {
        const displayValue = value || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Contact No.',
      dataIndex: 'contact_number',
      key: 'contact_number',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.contacts?.[0]?.contact_number || '') as string).localeCompare(b.contacts?.[0]?.contact_number || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.contacts?.[0]?.contact_number || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.email || a.contacts?.[0]?.email || '') as string).localeCompare(b.email || b.contacts?.[0]?.email || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.email || record.contacts?.[0]?.email || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Address',
      dataIndex: 'address1',
      key: 'address1',
      width: 240,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.contacts?.[0]?.address1 || '') as string).localeCompare(b.contacts?.[0]?.address1 || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.contacts?.[0]?.address1 || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Assessment',
      dataIndex: 'visa',
      key: 'visa',
      width: 200,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.reporting?.visa_text || '') as string).localeCompare(b.reporting?.visa_text || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.reporting?.visa_text || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Bank',
      dataIndex: 'bank',
      key: 'bank',
      width: 200,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.reporting?.bank_text || '') as string).localeCompare(b.reporting?.bank_text || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.reporting?.bank_text || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Bank No.',
      dataIndex: 'bank_number',
      key: 'bank_number',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        ((a.financials?.[0]?.bank_number || '') as string).localeCompare(b.financials?.[0]?.bank_number || ''),
      render: (_: string, record: Customer) => {
        const displayValue = record.financials?.[0]?.bank_number || '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor',
      dataIndex: 'agent_staff_id',
      key: 'agent_staff_id',
      width: 160,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.agent_staff_id || 0) - (b.agent_staff_id || 0),
      render: (value: number) => {
        const displayValue = value ? String(value) : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor Appoint Date',
      dataIndex: 'agent_appoint_date',
      key: 'agent_appoint_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.agent_appoint_date || '').localeCompare(b.agent_appoint_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Advisor Resign Date',
      dataIndex: 'agent_resign_date',
      key: 'agent_resign_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.agent_resign_date || '').localeCompare(b.agent_resign_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Staff',
      dataIndex: 'staff_id',
      key: 'staff_id',
      width: 160,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.staff_id || 0) - (b.staff_id || 0),
      render: (value: number) => {
        const displayValue = value ? String(value) : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Appoint Date',
      dataIndex: 'appoint_date',
      key: 'appoint_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.appoint_date || '').localeCompare(b.appoint_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Tax Resign Date',
      dataIndex: 'resign_date',
      key: 'resign_date',
      width: 180,
      visible: false,
      sorter: (a: Customer, b: Customer) =>
        (a.resign_date || '').localeCompare(b.resign_date || ''),
      render: (value: string) => {
        const displayValue = value ? dayjs(value).format('DD/MM/YYYY') : '-';
        return (
          <Tooltip title={displayValue} placement="topLeft">
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayValue}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
          width: col.width,
        onHeaderCell: col.onHeaderCell,
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: Customer) => record.name
            : col.dataIndex === 'ic_passport_no'
            ? (record: Customer) => record.ic_passport_no
            : col.dataIndex === 'tin_no'
            ? (record: Customer) => record.tin_no
            : col.dataIndex === 'nationality'
            ? (record: Customer) => record.nationality
            : col.dataIndex === 'race'
            ? (record: Customer) => record.race
            : col.dataIndex === 'sex'
            ? (record: Customer) => record.sex
            : col.dataIndex === 'engagement'
            ? (record: Customer) => record.engagement
            : col.dataIndex === 'agent'
            ? (record: Customer) => record.agent?.name
            : col.dataIndex === 'team'
            ? (record: Customer) => record.team?.name
            : col.dataIndex === 'type'
            ? (record: Customer) => record.type
            : col.dataIndex === 'contact_number'
            ? (record: Customer) => record.contacts?.[0]?.contact_number
            : col.dataIndex === 'email'
            ? (record: Customer) => record.email || record.contacts?.[0]?.email
            : col.dataIndex === 'address1'
            ? (record: Customer) => record.contacts?.[0]?.address1
            : col.dataIndex === 'visa'
            ? (record: Customer) => record.reporting?.visa_text
            : col.dataIndex === 'bank'
            ? (record: Customer) => record.reporting?.bank_text
            : col.dataIndex === 'bank_number'
            ? (record: Customer) => record.financials?.[0]?.bank_number
            : col.dataIndex === 'agent_staff_id'
            ? (record: Customer) => record.agent_staff_id
            : col.dataIndex === 'agent_appoint_date'
            ? (record: Customer) => record.agent_appoint_date
            : col.dataIndex === 'agent_resign_date'
            ? (record: Customer) => record.agent_resign_date
            : col.dataIndex === 'staff_id'
            ? (record: Customer) => record.staff_id
            : col.dataIndex === 'appoint_date'
            ? (record: Customer) => record.appoint_date
            : col.dataIndex === 'resign_date'
            ? (record: Customer) => record.resign_date
            : (undefined as unknown as (record: Customer) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Customer>(filteredCustomers, memoizedColumns as any);

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Personal
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
            {canCreate && (
              <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
                Import
              </Button>
            )}
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Customer
            </Button>
          )}
        </Space>
      </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              type={filter === 'potential' ? 'primary' : 'default'}
              onClick={() => setFilter('potential')}
            >
              Potential
            </Button>
            <Button 
              type={filter === 'active' ? 'primary' : 'default'}
              onClick={() => setFilter('active')}
            >
              Active
            </Button>
            <Button 
              type={filter === 'inactive' ? 'primary' : 'default'}
              onClick={() => setFilter('inactive')}
            >
              Inactive
            </Button>
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ y: 'calc(100vh - 400px)' }}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            hideSelectAll: true,
            columnWidth: 0,
            renderCell: () => null,
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'customer' : 'customers'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => setSelectedRowKeys([record.id]),
            onDoubleClick: canView ? () => handleEdit(record) : undefined,
            style: { cursor: canView ? 'pointer' : 'default' },
          })}
        />
    </div>
  );
};
